import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { BidStatus } from '../../types/auction';
import ReducerConstants from '../../constants/reducers';
import BidService from '../../../services/BidService';
import { bidSlice } from '../../../redux/slices/bid';
import { TradeViewVariant } from '../../types/trade';
import updateBidContextWithAuctionSummary from '../../helpers/updateBidContextWithAuctionSummary';
import useFetchAuctionSummary from '../../../components/FederatedBidModal/hooks/useFetchAuctionSummary';

interface IUseFetchAuctionSummary {
  tradeId: number;
  bidContext: any;
  setBidContext: (val: any) => void;
  isOmp: boolean;
  handleOmpBidding?: (val: any) => void;
  location?: TradeViewVariant;
  shouldFetchBidList?: boolean;
}

interface IAuctionSummary {
  upcomingProxyAmount?: number;
  upcomingBidAmount?: number;
  proxyAmount?: number;
  numBids: number;
  userBidStatus?: BidStatus;
  maxBid?: number;
  isTimerExtended?: boolean;
  activeExpiryTime?: string;
  tradeFloorPrice?: {
    hasFloorPrice: boolean;
    isCloseToFloorPrice: boolean;
    isFloorPriceMet: boolean;
  };
}

/**
 * This hook returns a function that can be evoked to fetch trade auctionSummary from trade details API & update the bidContext with it.
 * This hook also subscribes to biddingData redux state on OMP and updates bidContext if new state is available there.
 */
const useUpdateBidContextWithApi = ({
  tradeId,
  setBidContext,
  isOmp,
  handleOmpBidding,
  location,
  shouldFetchBidList
}: IUseFetchAuctionSummary) => {
  const dispatch = useDispatch();
  const bidService = BidService.get();
  const fetchAuctionSummary = useFetchAuctionSummary();

  const biddingData = useSelector(
    state => isOmp && state[ReducerConstants.BIDDING_DATA],
    shallowEqual
  );

  // When evoked, it updates bid context using auction summary and fetches bid list if shouldFetchBidList is true
  const updateBidContext = useCallback(
    async (auctionSummary: IAuctionSummary) => {
      updateBidContextWithAuctionSummary(setBidContext, auctionSummary);
      if (shouldFetchBidList) {
        const bidList = await bidService.getBidList(tradeId);
        dispatch(bidSlice.actions.setBidList(bidList));
      }
    },
    [bidService, dispatch, setBidContext, shouldFetchBidList, tradeId]
  );

  // Updates bidContext if bid has been placed from a different source (for ex. OMP list view)
  useEffect(() => {
    if (
      tradeId &&
      biddingData?.tradeId === tradeId &&
      biddingData?.source !== location
    ) {
      updateBidContext(biddingData?.auctionSummary);
    }
  }, [biddingData, location, tradeId, updateBidContext]);

  // When evoked, it fetches trade auctionSummary from trade details API & updates the bidContext with it
  return useCallback(async () => {
    try {
      const auctionSummary = await fetchAuctionSummary(tradeId);
      if (auctionSummary) {
        if (handleOmpBidding) {
          handleOmpBidding({
            tradeId,
            auctionSummary,
            source: location
          });
        }
        await updateBidContext(auctionSummary);
      }
      return null;
    } catch (e) {
      return null;
    }
  }, [
    fetchAuctionSummary,
    handleOmpBidding,
    location,
    tradeId,
    updateBidContext
  ]);
};

export default useUpdateBidContextWithApi;
